import React from 'react'
import { formatCurrency } from '../utils/currencyFormat'
import './FormattedTotal.css'

/**
 * Props for FormattedTotal component
 */
interface FormattedTotalProps {
  amount: number
  label?: string
  size?: 'small' | 'medium' | 'large'
  showLabel?: boolean
  fillParent?: boolean
  customClassName?: string
}

/**
 * Reusable component for displaying formatted currency totals
 * Uses the same styling as Project Offer Total
 *
 * @param amount - The numeric amount to display
 * @param label - Optional label text to display above the amount
 * @param size - Size variant: 'small' (13px), 'medium' (20px), 'large' (24px)
 * @param showLabel - Whether to show the label (default: true if label provided)
 * @param fillParent - Whether to fill parent container height with space-between layout (default: false)
 * @param customClassName - Optional custom CSS class to add to the amount div (e.g., 'font-size-18')
 *
 * Default styles: font-size: 15px, font-weight: 700, letter-spacing: 0.27px
 *
 * @example
 * // Default total with label (15px)
 * <FormattedTotal amount={15000.50} label="Project Offer Total" />
 *
 * @example
 * // Medium total without label
 * <FormattedTotal amount={8500.25} size="medium" showLabel={false} />
 *
 * @example
 * // Small total with custom label
 * <FormattedTotal amount={1200} label="Category Total" size="small" />
 *
 * @example
 * // List view with fillParent and 18px font size
 * <FormattedTotal amount={10598916.18} label="Total Costs" fillParent={true} customClassName="font-size-18" />
 */
const FormattedTotal: React.FC<FormattedTotalProps> = ({
  amount,
  label,
  size,
  showLabel = true,
  fillParent = false,
  customClassName
}) => {
  // Determine size class (default if not specified)
  const sizeClass = size || 'default'
  const labelSizeClass = size === 'small' ? 'size-small' : 'size-default'
  const hasLabelClass = showLabel && label ? 'has-label' : 'no-label'
  const fillParentClass = fillParent ? 'fill-parent' : ''

  // Boundary: coerce non-finite input (null/undefined/NaN/string) to 0 so the
  // page doesn't crash on unexpected upstream data. A console.warn keeps the
  // signal alive for engineers — silently displaying 0 would mask data bugs.
  const isAmountValid = Number.isFinite(amount)
  if (!isAmountValid) {
    console.warn('[FormattedTotal] received non-finite amount:', amount)
  }
  const formattedAmount = formatCurrency(isAmountValid ? amount : 0)
  const parts = formattedAmount.split(',')
  const integerPart = parts[0]
  const decimalPart = parts[1] ? `,${parts[1]}` : ''

  return (
    <div className={`formatted-total-wrapper ${fillParentClass}`.trim()}>
      {showLabel && label && (
        <p className={`formatted-total-label ${labelSizeClass}`}>
          {label}
        </p>
      )}
      <div className={`formatted-total-amount size-${sizeClass} ${hasLabelClass} ${customClassName || ''}`.trim()}>
        <p className="formatted-total-integer">
          {integerPart}
        </p>
        {decimalPart && (
          <p className="formatted-total-decimal">
            {decimalPart}
          </p>
        )}
      </div>
    </div>
  )
}

export default FormattedTotal
